'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { createSerializer, useQueryStates } from 'nuqs';
import { useEffect, useTransition } from 'react';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { setUserSettingsCookie } from '@/helpers/cookieStorage/userSettings/userSettingsCookie.client';
import { type MarketsMasterFilterState, MarketsUrlFilterParser } from '../config/config';
type UrlParams = ReturnType<typeof useQueryStates<typeof MarketsUrlFilterParser>>[0];
function urlParamsToMasterFilterState(params: UrlParams): MarketsMasterFilterState {
  return {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    chains: params.chains as ChainId[],
    collateralAssets: params.collateralAssetIdsFilter,
    loanAssets: params.loanAssetIdsFilter,
    curators: params.curators,
    totalMarketSize: {
      min: params.sizeUsd_gte ?? undefined,
      max: params.sizeUsd_lte ?? undefined
    },
    liquidity: {
      min: params.totalLiquidityUsd_gte ?? undefined,
      max: params.totalLiquidityUsd_lte ?? undefined
    },
    rate: {
      min: params.avgNetBorrowApy_gte ?? undefined,
      max: params.avgNetBorrowApy_lte ?? undefined
    },
    lltv: {
      min: params.lltv_gte ?? undefined,
      max: params.lltv_lte ?? undefined
    }
  };
}
function masterFilterStateToUrlParams(state: MarketsMasterFilterState): Partial<UrlParams> {
  return {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    chains: state.chains as number[],
    collateralAssetIdsFilter: state.collateralAssets,
    loanAssetIdsFilter: state.loanAssets,
    curators: state.curators,
    sizeUsd_gte: state.totalMarketSize.min ?? null,
    sizeUsd_lte: state.totalMarketSize.max ?? null,
    totalLiquidityUsd_gte: state.liquidity.min ?? null,
    totalLiquidityUsd_lte: state.liquidity.max ?? null,
    avgNetBorrowApy_gte: state.rate.min ?? null,
    avgNetBorrowApy_lte: state.rate.max ?? null,
    lltv_gte: state.lltv.min ?? null,
    lltv_lte: state.lltv.max ?? null
  };
}
const serializeMarketsFilters = createSerializer(MarketsUrlFilterParser);
export function useMarketsMasterFilter() {
  const $ = _c(13);
  const [urlParams, setUrlParams] = useQueryStates(MarketsUrlFilterParser);
  const [isTransitionPending, startTransition] = useTransition();
  let t0;
  let t1;
  if ($[0] !== urlParams) {
    t0 = () => {
      if (window.location.pathname !== "/variable") {
        return;
      }
      const qs = serializeMarketsFilters(urlParams);
      setUserSettingsCookie([PageKey.Borrow, "filters"], qs || null);
    };
    t1 = [urlParams];
    $[0] = urlParams;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== urlParams) {
    t2 = urlParamsToMasterFilterState(urlParams);
    $[3] = urlParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const masterFilterState = t2;
  let t3;
  if ($[5] !== setUrlParams) {
    t3 = newState => {
      startTransition(() => {
        setUrlParams(masterFilterStateToUrlParams(newState));
      });
    };
    $[5] = setUrlParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onMasterFilterChange = t3;
  let t4;
  if ($[7] !== isTransitionPending || $[8] !== masterFilterState || $[9] !== onMasterFilterChange || $[10] !== setUrlParams || $[11] !== urlParams) {
    t4 = {
      urlParams,
      setUrlParams,
      masterFilterState,
      onMasterFilterChange,
      isTransitionPending,
      startTransition
    };
    $[7] = isTransitionPending;
    $[8] = masterFilterState;
    $[9] = onMasterFilterChange;
    $[10] = setUrlParams;
    $[11] = urlParams;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}